import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuthToken } from "../utils/authToken";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, ArrowLeft, MapPin, CreditCard, Package,
  CheckCircle, Truck, Home, Copy, Check, BookOpen,
} from "lucide-react";

const STATUS_META = {
  confirmed:  { label: "Confirmed",  color: "#2563EB", bg: "rgba(37,99,235,0.10)"  },
  processing: { label: "Processing", color: "#B07D1C", bg: "rgba(176,125,28,0.12)" },
  shipped:    { label: "Shipped",    color: "#7C5D1A", bg: "rgba(124,93,26,0.12)"  },
  delivered:  { label: "Delivered",  color: "#3A6645", bg: "rgba(58,102,69,0.12)"  },
  cancelled:  { label: "Cancelled",  color: "#8B3A2A", bg: "rgba(139,58,42,0.12)" },
};

const STEPS = [
  { key: "confirmed",  icon: CheckCircle, label: "Confirmed"  },
  { key: "processing", icon: Package,     label: "Processing" },
  { key: "shipped",    icon: Truck,       label: "Shipped"    },
  { key: "delivered",  icon: Home,        label: "Delivered"  },
];

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "3px" }}>{label}</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value || "—"}</div>
      </div>
      {value && (
        <button onClick={copy} style={{
          flexShrink: 0, width: "28px", height: "28px", borderRadius: "var(--radius)",
          border: "1px solid var(--dust-2)", background: copied ? "var(--success-faint)" : "var(--cream-2)",
          color: copied ? "var(--success)" : "var(--muted)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s",
        }}>
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user) fetchOrder();
  }, [user, navigate, orderId]);

  async function fetchOrder() {
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Order not found");
      setOrder(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight: "calc(100vh - var(--nav-h))", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem", background: "var(--cream)" }}>
      <Loader2 size={36} color="var(--gold)" style={{ animation: "spin 1s linear infinite" }} />
      <span style={{ color: "var(--muted)", fontSize: "0.9rem" }}>Loading order…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !order) return (
    <div style={{ minHeight: "calc(100vh - var(--nav-h))", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem", background: "var(--cream)", padding: "2rem", textAlign: "center" }}>
      <Package size={48} color="var(--rust)" strokeWidth={1.5} />
      <h2 style={{ fontFamily: "var(--font-display)", color: "var(--ink)", margin: 0 }}>Order Not Found</h2>
      <p style={{ color: "var(--muted)", fontSize: "0.9rem", margin: 0 }}>{error || "We couldn't find this order."}</p>
      <button onClick={() => navigate("/orders")} style={{
        marginTop: "0.5rem", padding: "10px 24px",
        background: "none", border: "1px solid var(--gold)", color: "var(--gold)",
        borderRadius: "var(--radius-full)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
      }}>
        Back to My Orders
      </button>
    </div>
  );

  const statusIdx = STEPS.findIndex(s => s.key === order.status);
  const statusMeta = STATUS_META[order.status] || { label: order.status, color: "var(--muted)", bg: "var(--dust)" };

  return (
    <div className="page-enter" style={{ minHeight: "calc(100vh - var(--nav-h))", background: "var(--cream)", padding: "2rem 1.25rem 6rem" }}>
      <div style={{ maxWidth: "820px", margin: "0 auto" }}>

        {/* Back */}
        <button onClick={() => navigate("/orders")} style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: "none", border: "none", color: "var(--muted)",
          fontFamily: "var(--font-body)", fontSize: "0.88rem", cursor: "pointer",
          padding: "0 0 1.5rem", transition: "color 0.2s",
        }}
          onMouseEnter={e => e.currentTarget.style.color = "var(--gold)"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}
        >
          <ArrowLeft size={15} /> My Orders
        </button>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.75rem" }}>
          <div>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.5rem,4vw,1.9rem)", fontWeight: 700, color: "var(--ink)", margin: "0 0 4px" }}>
              Order #{order.id.slice(-8).toUpperCase()}
            </h1>
            <p style={{ color: "var(--muted)", fontSize: "0.85rem", margin: 0 }}>
              Placed {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              {" · "}
              {new Date(order.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "6px 16px", borderRadius: "var(--radius-full)",
            fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em",
            color: statusMeta.color, background: statusMeta.bg, textTransform: "uppercase",
          }}>
            {statusMeta.label}
          </span>
        </div>

        {/* Stepper */}
        <div style={{
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: "var(--radius-lg)", padding: "1.5rem 1.25rem",
          marginBottom: "1.5rem", boxShadow: "var(--shadow)",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", position: "relative" }}>
            {/* connector */}
            <div style={{
              position: "absolute", top: "20px",
              left: "calc(12.5%)", right: "calc(12.5%)",
              height: "2px", zIndex: 0,
              background: `linear-gradient(to right, var(--gold) ${(statusIdx / (STEPS.length - 1)) * 100}%, var(--dust-2) ${(statusIdx / (STEPS.length - 1)) * 100}%)`,
              transition: "background 0.5s",
            }} />
            {STEPS.map((step, i) => {
              const done = i <= statusIdx;
              const active = i === statusIdx;
              const Icon = step.icon;
              const hist = order.statusHistory?.find(h => h.status === step.key);
              return (
                <div key={step.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", position: "relative", zIndex: 1 }}>
                  <div className="od-stepper-icon" style={{
                    width: "40px", height: "40px", borderRadius: "50%",
                    background: done ? (active ? "var(--gold)" : "var(--success)") : "var(--card-bg)",
                    border: `2px solid ${done ? (active ? "var(--gold)" : "var(--success)") : "var(--dust-2)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: done ? "#fff" : "var(--muted-2)",
                    boxShadow: active ? "var(--shadow-gold)" : "none",
                    transition: "all 0.3s",
                  }}>
                    <Icon size={16} strokeWidth={done ? 2.5 : 1.8} />
                  </div>
                  <div className="od-stepper-label" style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.75rem", fontWeight: done ? 700 : 500, color: done ? "var(--ink)" : "var(--muted-2)" }}>
                      {step.label}
                    </div>
                    {hist && (
                      <div style={{ fontSize: "0.65rem", color: "var(--muted)", marginTop: "2px" }}>
                        {new Date(hist.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main grid */}
        <div className="order-detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "1.25rem", alignItems: "start" }}>

          {/* LEFT: Items */}
          <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "center", gap: "8px" }}>
              <Package size={15} color="var(--gold)" />
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--ink)", fontSize: "0.95rem" }}>Items</span>
              <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--muted)" }}>{order.items?.length} item{order.items?.length !== 1 ? "s" : ""}</span>
            </div>

            <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              {(order.items || []).map((item, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", paddingBottom: idx < order.items.length - 1 ? "1rem" : 0, borderBottom: idx < order.items.length - 1 ? "1px solid var(--card-border)" : "none" }}>
                  <div style={{
                    width: "44px", height: "60px", borderRadius: "4px", flexShrink: 0,
                    background: "linear-gradient(135deg, #1A1108, #C8941F)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <BookOpen size={16} color="rgba(255,255,255,0.65)" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: "0.9rem", marginBottom: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{item.author}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: "var(--ink)", fontSize: "0.92rem" }}>₹{(item.price / 100).toFixed(0)}</div>
                    <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: "2px" }}>qty {item.quantity}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid var(--card-border)", background: "var(--cream-2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.83rem", color: "var(--muted)", marginBottom: "4px" }}>
                <span>Subtotal</span>
                <span>₹{((order.items || []).reduce((s, i) => s + (i.price * i.quantity), 0) / 100).toFixed(0)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.83rem", color: "var(--muted)", marginBottom: "10px" }}>
                <span>Shipping</span>
                <span>{order.total > (order.items || []).reduce((s,i) => s + (i.price * i.quantity), 0) ? `₹${((order.total - (order.items||[]).reduce((s,i)=>s+(i.price*i.quantity),0))/100).toFixed(0)}` : "Free"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, color: "var(--ink)" }}>Total Paid</span>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.4rem", color: "var(--gold)" }}>₹{(order.total / 100).toFixed(0)}</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Address + Payment */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

            {/* Shipping address */}
            <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
              <div style={{ padding: "0.9rem 1.25rem", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "center", gap: "8px" }}>
                <MapPin size={14} color="var(--gold)" />
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--ink)", fontSize: "0.9rem" }}>Shipping Address</span>
              </div>
              {order.address ? (
                <div style={{ padding: "1rem 1.25rem" }}>
                  <div style={{ fontWeight: 700, color: "var(--ink)", fontSize: "0.92rem", marginBottom: "4px" }}>{order.address.name}</div>
                  <div style={{ fontSize: "0.82rem", color: "var(--muted)", lineHeight: 1.65 }}>
                    {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />
                    {order.address.city}, {order.address.state}<br />
                    {order.address.pincode}<br />
                    <span style={{ marginTop: "4px", display: "inline-block" }}>📞 {order.address.phone}</span>
                  </div>
                </div>
              ) : (
                <div style={{ padding: "1rem 1.25rem", color: "var(--muted)", fontSize: "0.85rem" }}>No address on record.</div>
              )}
            </div>

            {/* Payment info */}
            <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
              <div style={{ padding: "0.9rem 1.25rem", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "center", gap: "8px" }}>
                <CreditCard size={14} color="var(--gold)" />
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--ink)", fontSize: "0.9rem" }}>Payment</span>
              </div>
              <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
                <CopyField label="Payment ID" value={order.payment_id} />
                <div>
                  <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "3px" }}>Gateway</div>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--ink)", textTransform: "capitalize" }}>{order.payment_gateway || "Razorpay"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @media (max-width: 820px) {
          .order-detail-grid { grid-template-columns: 1fr !important; }
          .od-stepper-label { font-size: 0.65rem !important; }
        }
        @media (max-width: 480px) {
          .od-stepper-label { display: none !important; }
          .od-stepper-icon { width: 34px !important; height: 34px !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pageEnter {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .page-enter { animation: pageEnter 0.32s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </div>
  );
}
